"use client";

import type React from "react";
import { useState, useEffect, useRef } from "react";
import { motion } from "motion/react";
import {
  Send,
  ArrowLeft,
  Sparkles,
  History,
  Trash2,
  MessageCircle,
  Plus,
} from "lucide-react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import LoginRequired from "@/components/auth/login-required";
import { useSession } from "next-auth/react";

interface ChatHistory {
  id: string;
  title: string;
  messageCount: number;
  lastMessage: string;
  createdAt: string;
  updatedAt: string;
}

export default function AIChatPage() {
  const { data: session } = useSession();
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [chatHistory, setChatHistory] = useState<ChatHistory[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [messages, setMessages] = useState<
    { role: "user" | "assistant"; content: string }[]
  >([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = { role: "user" as const, content: input.trim() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setIsLoading(true);

    try {
      const response = await fetch("/api/ai/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          messages: newMessages,
          chatId: currentChatId || null,
          type: "general",
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to send message");
      }

      if (!response.body) {
        throw new Error("No response body");
      }

      // Handle streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = "";

      // Add assistant message placeholder
      setMessages([...newMessages, { role: "assistant", content: "" }]);

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          assistantMessage += chunk;

          // Update the assistant message with accumulated content
          setMessages([
            ...newMessages,
            { role: "assistant", content: assistantMessage },
          ]);
        }

        // Reload chat history after completion
        setTimeout(() => loadChatHistory(), 500);
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
      console.error("Chat error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  };

  // Load chat history on component mount
  useEffect(() => {
    loadChatHistory();
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isLoading]);

  const loadChatHistory = async () => {
    try {
      const response = await fetch("/api/ai/chats");
      if (response.ok) {
        const data = await response.json();
        setChatHistory(data.chats || []);
      }
    } catch (error) {
      console.error("Failed to load chat history:", error);
    }
  };

  const loadChat = async (chatId: string) => {
    try {
      const response = await fetch(`/api/ai/chats/${chatId}`);
      if (response.ok) {
        const data = await response.json();
        setMessages(data.chat.messages || []);
        setCurrentChatId(chatId);
        setShowHistory(false);
      }
    } catch (error) {
      console.error("Failed to load chat:", error);
      toast.error("Failed to load chat");
    }
  };

  const deleteChat = async (chatId: string) => {
    try {
      const response = await fetch(`/api/ai/chats/${chatId}`, {
        method: "DELETE",
      });
      if (response.ok) {
        toast.success("Chat deleted successfully");
        loadChatHistory();
        if (currentChatId === chatId) {
          startNewChat();
        }
      } else {
        throw new Error("Failed to delete chat");
      }
    } catch (error) {
      console.error("Failed to delete chat:", error);
      toast.error("Failed to delete chat");
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setCurrentChatId(null);
    setShowHistory(false);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    await handleSubmit(e);
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <LoginRequired
      message="Sign in to access our AI Chat Assistant and save your conversation history."
      feature="chat"
    >
      <div className="container mx-auto py-10 px-4 md:px-6">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          transition={{ duration: 0.5 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <Link href="/ai">
                <Button variant="outline" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to AI Hub
                </Button>
              </Link>
              <div className="flex items-center space-x-2">
                <Sparkles className="w-6 h-6 text-primary" />
                <h1 className="text-3xl font-bold">AI Chat Assistant</h1>
                <Badge variant="secondary" className="text-xs">
                  ⚡ Streaming
                </Badge>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowHistory(!showHistory)}
              >
                <History className="w-4 h-4 mr-2" />
                History ({chatHistory.length})
              </Button>
              <Button variant="outline" size="sm" onClick={startNewChat}>
                <Plus className="w-4 h-4 mr-2" />
                New Chat
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Chat History Sidebar */}
            {showHistory && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="lg:col-span-1"
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm flex items-center">
                      <History className="w-4 h-4 mr-2" />
                      Chat History
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="max-h-96 overflow-y-auto">
                      {chatHistory.length === 0 ? (
                        <p className="text-sm text-muted-foreground p-4">
                          No chat history yet
                        </p>
                      ) : (
                        chatHistory.map((chat) => (
                          <div
                            key={chat.id}
                            className={`p-3 border-b hover:bg-muted/50 cursor-pointer transition-colors group ${
                              currentChatId === chat.id ? "bg-muted" : ""
                            }`}
                            onClick={() => loadChat(chat.id)}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">
                                  {chat.title || "Untitled Chat"}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {chat.messageCount || 0} messages
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(
                                    chat.updatedAt
                                  ).toLocaleDateString()}
                                </p>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteChat(chat.id);
                                }}
                                className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Main Chat Interface */}
            <div className={showHistory ? "lg:col-span-3" : "lg:col-span-4"}>
              <Card className="h-[600px] flex flex-col">
                <CardHeader>
                  <CardTitle className="flex items-center text-lg">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    AI Learning Assistant
                  </CardTitle>
                </CardHeader>

                <CardContent className="flex-1 overflow-hidden p-0">
                  <div className="flex flex-col h-full">
                    <div className="flex-1 overflow-y-auto p-6 space-y-4 min-h-0">
                      {messages.length === 0 ? (
                        <div className="text-center py-12 h-full flex flex-col justify-center">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xl font-bold mx-auto mb-4">
                            AI
                          </div>
                          <h3 className="text-lg font-semibold mb-2">
                            Ready to help you learn!
                          </h3>
                          <p className="text-muted-foreground max-w-md mx-auto mb-6">
                            I'm your AI learning assistant with{" "}
                            <strong>real-time streaming responses</strong>. Ask
                            me anything about your studies, homework, or general
                            questions.
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-lg mx-auto">
                            <button
                              onClick={() =>
                                setInput("Explain how photosynthesis works")
                              }
                              className="p-3 text-sm bg-card border rounded-lg hover:bg-muted/50 transition-colors text-left"
                            >
                              📚 Explain a concept
                            </button>
                            <button
                              onClick={() =>
                                setInput("Help me solve this math problem")
                              }
                              className="p-3 text-sm bg-card border rounded-lg hover:bg-muted/50 transition-colors text-left"
                            >
                              🧮 Solve problems
                            </button>
                            <button
                              onClick={() =>
                                setInput("Can you help me write an essay?")
                              }
                              className="p-3 text-sm bg-card border rounded-lg hover:bg-muted/50 transition-colors text-left"
                            >
                              ✍️ Writing help
                            </button>
                            <button
                              onClick={() =>
                                setInput("What are some study tips?")
                              }
                              className="p-3 text-sm bg-card border rounded-lg hover:bg-muted/50 transition-colors text-left"
                            >
                              💡 Study guidance
                            </button>
                          </div>
                        </div>
                      ) : (
                        messages.map((message, index) => (
                          <div
                            key={index}
                            className={`flex items-start space-x-3 ${
                              message.role === "user"
                                ? "justify-end"
                                : "justify-start"
                            } mb-4`}
                          >
                            {message.role === "assistant" && (
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                                AI
                              </div>
                            )}
                            <div
                              className={`max-w-[70%] rounded-lg px-4 py-3 ${
                                message.role === "user"
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-muted/50 text-foreground"
                              }`}
                            >
                              {message.role === "assistant" ? (
                                <div className="prose prose-sm dark:prose-invert max-w-none">
                                  <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                      h1: ({ children }) => (
                                        <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-3">
                                          {children}
                                        </h1>
                                      ),
                                      h2: ({ children }) => (
                                        <h2 className="text-md font-semibold text-slate-800 dark:text-slate-200 mb-2">
                                          {children}
                                        </h2>
                                      ),
                                      h3: ({ children }) => (
                                        <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                          {children}
                                        </h3>
                                      ),
                                      p: ({ children }) => (
                                        <p className="text-slate-700 dark:text-slate-200 mb-2 leading-relaxed">
                                          {children}
                                        </p>
                                      ),
                                      ul: ({ children }) => (
                                        <ul className="text-slate-700 dark:text-slate-200 ml-3 mb-2 space-y-1">
                                          {children}
                                        </ul>
                                      ),
                                      ol: ({ children }) => (
                                        <ol className="text-slate-700 dark:text-slate-200 ml-3 mb-2 space-y-1">
                                          {children}
                                        </ol>
                                      ),
                                      li: ({ children }) => (
                                        <li className="marker:text-blue-600 dark:marker:text-blue-400 text-sm">
                                          {children}
                                        </li>
                                      ),
                                      code: ({ children }) => (
                                        <code className="bg-slate-100 dark:bg-slate-600 text-slate-800 dark:text-slate-200 px-1.5 py-0.5 rounded font-mono text-xs">
                                          {children}
                                        </code>
                                      ),
                                      blockquote: ({ children }) => (
                                        <blockquote className="border-l-3 border-blue-400 pl-3 italic text-slate-600 dark:text-slate-300 my-2">
                                          {children}
                                        </blockquote>
                                      ),
                                      strong: ({ children }) => (
                                        <strong className="text-slate-800 dark:text-slate-100 font-semibold">
                                          {children}
                                        </strong>
                                      ),
                                    }}
                                  >
                                    {message.content}
                                  </ReactMarkdown>
                                </div>
                              ) : (
                                <p className="text-sm">{message.content}</p>
                              )}
                            </div>
                            {message.role === "user" && (
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-blue-600 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                                {session?.user?.name
                                  ? session.user.name.charAt(0).toUpperCase()
                                  : session?.user?.email
                                    ? session.user.email.charAt(0).toUpperCase()
                                    : "U"}
                              </div>
                            )}
                          </div>
                        ))
                      )}

                      {isLoading && (
                        <div className="flex items-start space-x-3 justify-start mb-4">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold">
                            AI
                          </div>
                          <div className="flex items-center space-x-2 p-4 rounded-lg bg-muted/50 max-w-xs">
                            <div className="flex space-x-1">
                              <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
                              <div
                                className="w-2 h-2 bg-primary rounded-full animate-bounce"
                                style={{ animationDelay: "0.1s" }}
                              ></div>
                              <div
                                className="w-2 h-2 bg-primary rounded-full animate-bounce"
                                style={{ animationDelay: "0.2s" }}
                              ></div>
                            </div>
                            <span className="text-sm text-muted-foreground">
                              Streaming...
                            </span>
                          </div>
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                  </div>
                </CardContent>

                <CardFooter>
                  <form onSubmit={onSubmit} className="flex space-x-2 w-full">
                    <Input
                      value={input}
                      onChange={handleInputChange}
                      placeholder="Ask me anything... (real-time streaming enabled)"
                      disabled={isLoading}
                      className="flex-1"
                    />
                    <Button
                      type="submit"
                      disabled={!input.trim() || isLoading}
                      size="icon"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </form>
                </CardFooter>
              </Card>
            </div>
          </div>
        </motion.div>
      </div>
    </LoginRequired>
  );
}
